import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  EsOntologyIndex,
  useCreateEsIndex,
  useUpdateEsIndex,
} from "@/service-hooks/use-get-es-ontology-service";

interface EsIndexDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  dataLakeId: string;
  // When supplied the dialog is in edit mode — `id` is used for the PUT,
  // and the form is pre-populated with the existing values.
  existing?: EsOntologyIndex;
  onSaved?: () => void;
}

interface FormState {
  name: string;
  shards: string;
  replicas: string;
  defaultAnalyzer: string;
  sourcePath: string;
}

const EMPTY: FormState = {
  name: "",
  shards: "",
  replicas: "",
  defaultAnalyzer: "",
  sourcePath: "",
};

export default function EsIndexDialog({
  isOpen,
  onClose,
  projectUuid,
  dataLakeId,
  existing,
  onSaved,
}: EsIndexDialogProps) {
  const [form, setForm] = useState<FormState>({ ...EMPTY });
  const createIndex = useCreateEsIndex();
  const updateIndex = useUpdateEsIndex();
  const isEdit = !!existing?.id;
  const isPending = createIndex.isPending || updateIndex.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      name: existing?.name ?? "",
      shards: existing?.shards ?? "",
      replicas: existing?.replicas ?? "",
      defaultAnalyzer: existing?.defaultAnalyzer ?? "",
      sourcePath: existing?.sourcePath ?? "",
    });
  }, [isOpen, existing]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    try {
      if (isEdit && existing?.id) {
        await updateIndex.mutateAsync({
          id: existing.id,
          projectUuid,
          dataLakeId,
          data: {
            name: form.name.trim(),
            shards: form.shards.trim() || undefined,
            replicas: form.replicas.trim() || undefined,
            defaultAnalyzer: form.defaultAnalyzer.trim() || undefined,
            sourcePath: form.sourcePath.trim() || undefined,
          },
        });
      } else {
        await createIndex.mutateAsync({
          projectUuid,
          dataLakeId,
          name: form.name.trim(),
          shards: form.shards.trim() || undefined,
          replicas: form.replicas.trim() || undefined,
          defaultAnalyzer: form.defaultAnalyzer.trim() || undefined,
          sourcePath: form.sourcePath.trim() || undefined,
        });
      }
      onSaved?.();
      onClose();
    } catch {
      // toast handled in mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit ES Index" : "Create ES Index"}</DialogTitle>
          <DialogDescription className="sr-only">
            Configure the fields for this ES index.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 overflow-y-auto flex-1 pr-1 ${CUSTOM_SCROLLBAR}`}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">
              Name <span className="text-error">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="e.g. anz_company_active_index"
              className="bg-surface text-white"
            />
            {isEdit && (
              <p className="text-xs text-muted">
                Renaming the index changes the MERGE key used by future ingests
                — re-uploading the original mapping/settings with the old name
                will create a new index node.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Shards</label>
              <Input
                value={form.shards}
                onChange={(e) => update("shards", e.target.value)}
                placeholder="e.g. 1"
                className="bg-surface text-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-white">Replicas</label>
              <Input
                value={form.replicas}
                onChange={(e) => update("replicas", e.target.value)}
                placeholder="e.g. 1"
                className="bg-surface text-white"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Default Analyzer</label>
            <Input
              value={form.defaultAnalyzer}
              onChange={(e) => update("defaultAnalyzer", e.target.value)}
              placeholder="e.g. standard"
              className="bg-surface text-white"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-white">Source Path</label>
            <Input
              value={form.sourcePath}
              onChange={(e) => update("sourcePath", e.target.value)}
              placeholder="Original file path (optional)"
              className="bg-surface text-white"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-surface-border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.name.trim() || isPending}
            className="bg-primary hover:bg-primary-dark text-white"
          >
            {isPending ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
